import { useTranslation } from 'react-i18next';
import type { Editor } from '@tiptap/core';
import { Heading1, Heading2, Heading3, List, ListOrdered, Quote } from 'lucide-react';
import { Separator } from '@/shared/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ToolbarButton } from './ToolbarButton';

interface FloatingToolbarProps {
  editor: Editor;
}

/**
 * Quick-insert toolbar shown when the cursor is on an empty line.
 * Mobile: single "+" icon. md+: heading + list + quote shortcuts.
 */
export function FloatingToolbar({ editor }: FloatingToolbarProps) {
  const { t } = useTranslation('richText');

  const insertSlashTrigger = () => {
    editor.chain().focus().insertContent('/').run();
  };

  return (
    <div
      data-testid="rich-text-floating-toolbar"
      className="flex items-center gap-1 rounded-xl border border-border/60 bg-popover px-1.5 py-1 shadow-md ring-1 ring-border/30"
    >
      {/* Slash trigger — always visible, pill style to signal keyboard affordance */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={t('floating.slash')}
            data-testid="rich-text-floating-slash"
            onClick={insertSlashTrigger}
            className="flex h-7 items-center rounded-md border border-border bg-muted px-2 font-mono text-sm font-medium text-muted-foreground transition-colors duration-100 hover:bg-accent hover:text-accent-foreground"
          >
            /
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {t('floating.slash')}
        </TooltipContent>
      </Tooltip>

      {/* Block shortcuts — hidden on mobile */}
      <div className="hidden md:flex items-center gap-1">
        <Separator orientation="vertical" className="mx-1.5 h-4 opacity-40" />

        <ToolbarButton
          icon={<Heading1 className="h-4 w-4" />}
          label={t('floating.heading1')}
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().setHeading({ level: 1 }).run()}
          testId="rich-text-floating-heading1"
        />
        <ToolbarButton
          icon={<Heading2 className="h-4 w-4" />}
          label={t('floating.heading2')}
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().setHeading({ level: 2 }).run()}
          testId="rich-text-floating-heading2"
        />
        <ToolbarButton
          icon={<Heading3 className="h-4 w-4" />}
          label={t('floating.heading3')}
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().setHeading({ level: 3 }).run()}
          testId="rich-text-floating-heading3"
        />

        <Separator orientation="vertical" className="mx-1.5 h-4 opacity-40" />

        <ToolbarButton
          icon={<List className="h-4 w-4" />}
          label={t('floating.bulletList')}
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          testId="rich-text-floating-bullet-list"
        />
        <ToolbarButton
          icon={<ListOrdered className="h-4 w-4" />}
          label={t('floating.orderedList')}
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          testId="rich-text-floating-ordered-list"
        />
        <ToolbarButton
          icon={<Quote className="h-4 w-4" />}
          label={t('floating.quote')}
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          testId="rich-text-floating-quote"
        />
      </div>
    </div>
  );
}
